<script>
import { mapActions } from "vuex";

import InfoTooltip from "@/components/InfoTooltip";
import AutoRefreshToggle from "@/components/AutoRefreshToggle";
import HashrateChart from "@/components/HashrateChart";
import { formatHashrate } from "@/utils";

export default {
  // Included components
  components: {
    InfoTooltip,
    AutoRefreshToggle,
    HashrateChart,
  },

  data() {
    return {
      selectedTeams: [],
      team_stats: [],
      team_shares: [],
      team_alerts: [],
      loadingState: true,
    };
  },

  async created() {
    let selected = this.$route.params.teams;
    if (!selected) {
      // go back to homepage if there's no teams to display
      this.$router.push("/");
    }

    this.selectedTeams = selected;
    await this.fetchTeamData();
  },

  methods: {
    async fetchTeamData() {
      // get team statistics and shares for each team
      this.loadingState = true;
      let fetches = [];
      this.selectedTeams.map((curr_team) => {
        fetches.push(
          this.getTeamStatistics({ team_obj: curr_team, save: false })
        );
        fetches.push(this.getTeamShares({ team_obj: curr_team, save: false }));
        fetches.push(this.getAlerts({ team_obj: curr_team, save: false }));
      });

      let res = await Promise.all(fetches);
      let team_statistics = [];
      // separate data in groups of 3 (3 data elements per team)
      for (let i = 0; i < res.length; i += 3)
        team_statistics.push(res.slice(i, i + 3));

      // assign elements to state
      team_statistics.map((data, idx) => {
        this.team_stats[idx] = data[0];
        this.team_shares[idx] = data[1];
        this.team_alerts[idx] = data[2];
      });
      this.loadingState = false;
    },

    async updateHashrateChartData() {
      // same function as overview, but applied to multiple teams
      let fetches = [];
      this.selectedTeams.map((curr_team) => {
        fetches.push(this.getTeamShares({ team_obj: curr_team, save: false }));
      });

      let res = await Promise.all(fetches);
      res.map((arr, idx) => {
        this.team_shares[idx] = arr;
      });
    },

    updateStatistics() {
      // same function as overview, but applied to multiple teams
      let fetches = [];
      this.selectedTeams.map((curr_team) => {
        fetches.push(
          this.getTeamStatistics({ team_obj: curr_team, save: false })
        );
        fetches.push(this.getAlerts({ team_obj: curr_team, save: false }));
      });

      Promise.all(fetches).then((value) => {
        let team_statistics = [];
        for (let i = 0; i < value.length; i += 2)
          team_statistics.push(value.slice(i, i + 2));

        team_statistics.map((data, idx) => {
          this.team_stats[idx] = data[0];
          this.team_alerts[idx] = data[1];
        });
      });
    },

    triggeringAlertsCount(idx) {
      return Object.values(this.team_alerts[idx].alerts).filter(
        (alert) => alert.triggering && alert.enabled
      ).length;
    },

    // Helper functions and mapped state actions
    formatHashrate,
    ...mapActions(["getTeamStatistics", "getTeamShares", "getAlerts"]),
  },
};
</script>

<template>
  <b-container>
    <b-row class="mb-2">
      <b-col class="mt-4">
        <h1>Overview</h1>
        <AutoRefreshToggle
          id="fullscreen-toggle"
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
      </b-col>
    </b-row>
    <b-row v-if="this.loadingState">
      <b-col class="text-center">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
          ></b-spinner>
        </div>
        <b>Loading data for teams...</b>
      </b-col>
    </b-row>
    <b-row v-else>
      <b-col lg="6" xl="6" v-for="(team, idx) in this.selectedTeams" :key="idx">
        <b-card class="mb-3">
          <b-card-title>{{ team.handle }}</b-card-title>
          <b-row>
            <b-col lg="3" xl="3">
              <b-card-text>
                <b>30m Hashrate</b>
                &nbsp;
                <InfoTooltip text="30 minute average hashrate" />
                <p>{{ formatHashrate(team_stats[idx].hashrate) }}</p>
              </b-card-text>
            </b-col>
            <b-col lg="3" xl="3">
              <b-card-text>
                <b>Active Workers</b>
                <p>
                  {{ team_stats[idx].active_count }} /
                  {{ team_stats[idx].count }}
                </p>
              </b-card-text>
            </b-col>
            <b-col lg="3" xl="3">
              <b-card-text>
                <b>Total Workers</b>
                <p>{{ team_stats[idx].count }}</p>
              </b-card-text>
            </b-col>
            <b-col lg="3" xl="3">
              <b-card-text>
                <b>Alert Status</b>
                <p v-if="triggeringAlertsCount(idx) > 0">
                  <b-badge variant="danger">ALERT</b-badge>
                  &times;
                  {{ triggeringAlertsCount(idx) }}
                </p>
                <p v-else>
                  <b-badge variant="success">OK</b-badge>
                </p>
              </b-card-text>
            </b-col>
          </b-row>

          <HashrateChart
            :data="
              team_shares[idx]
                ? team_shares[idx].map((share) => [share[0], share[1] / 300])
                : []
            "
            :canExport="false"
          />
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<style scoped>
#fullscreen-toggle {
  visibility: hidden;
}
</style>
